import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials, videoTestimonials } from '../data/mock';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language].testimonials;

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.video-testimonial-card',
        { opacity: 0, scale: 0.9, y: 50 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 1,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-gradient-to-b from-red-50 to-white dark:from-black dark:to-gray-900 transition-colors duration-300 w-full overflow-x-hidden">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.title} <span className="text-red-600 dark:text-red-500">{t.highlight}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.subtitle}
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-end gap-2 mb-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('left')}
            className="rounded-full border-gray-300 dark:border-gray-600 hover:border-red-500 hover:text-red-500 dark:text-white dark:hover:text-red-400"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('right')}
            className="rounded-full border-gray-300 dark:border-gray-600 hover:border-red-500 hover:text-red-500 dark:text-white dark:hover:text-red-400"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Video Carousel - Full Width Bleed */}
      <div className="relative w-full overflow-hidden">
        <div 
          ref={scrollRef}
          className="flex gap-8 py-8 px-4 md:px-12 lg:px-24 overflow-x-auto snap-x snap-mandatory scrollbar-hide no-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
            {videoTestimonials.map((video, index) => (
                <div key={video.id} className="video-testimonial-card flex-shrink-0 snap-center snap-always relative group">
                    {/* Decorative background glow */}
                    <div className="absolute -inset-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-[2.5rem] blur-xl opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                    
                    {/* Main Video Container */}
                    <div className="relative bg-white dark:bg-gray-800 rounded-[2.5rem] p-3 shadow-2xl border border-white/50 dark:border-gray-700/50 backdrop-blur-sm overflow-hidden">
                        <div className="aspect-[9/16] w-[280px] sm:w-[320px] md:w-[360px] rounded-[2rem] overflow-hidden bg-black shadow-inner border border-gray-100 dark:border-gray-900">
                            <iframe
                                className="w-full h-full scale-[1.01]" // Slight scale to hide any tiny edge artifacts
                                src={`https://www.youtube.com/embed/${video.videoId}?rel=0&modestbranding=1&controls=1`}
                                title={video.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>

                        {/* Title Overlay at the top of the video (Wrapped Full Title) */}
                        <div className="absolute inset-x-3 top-3 p-6 pt-8 bg-gradient-to-b from-black/80 via-black/40 to-transparent rounded-t-[1.8rem] pointer-events-none z-10">
                             <h3 className="text-white text-base md:text-lg font-bold leading-tight drop-shadow-md" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                {t.videoTitles[index]}
                             </h3>
                        </div>
                    </div>
                </div>
            ))}
        </div>
      </div>

      {/* High-Converting CTA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 text-center">
          <div className="inline-block max-w-full p-0.5 rounded-full bg-gradient-to-r from-red-600 to-orange-500 shadow-xl shadow-red-500/10 hover:shadow-red-500/30 transition-shadow duration-500">
              <Button
                  onClick={() => document.getElementById('enquiry')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:bg-transparent hover:text-white px-8 sm:px-12 py-3 sm:py-6 rounded-full text-base sm:text-lg font-bold transition-all duration-300 group overflow-hidden relative"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                      {t.cta}
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
              </Button>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-6 text-sm sm:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {t.ctaSubtitle.split('{highlight}').map((part, i, arr) => (
                  <span key={i}>
                      {part}
                      {i < arr.length - 1 && <span className="font-bold text-red-600 dark:text-red-500">{t.ctaHighlight}</span>}
                  </span>
              ))}
          </p>
      </div>
    </section>
  );
};

export default TestimonialsSection;
